<template>
    <div class="sel-chips">
        <div v-for="group in groups" :key="group.id" class="sel-chips-group">
            <div v-if="group.label" class="sel-chips-group-title">{{ group.label }}</div>
            <div class="sel-chips-row" role="group">
                <button
                    v-for="opt in group.options"
                    :key="opt.id"
                    type="button"
                    class="sel-chip"
                    :class="{ selected: isSelected(opt.id), unavailable: !isAvailable(opt.id) }"
                    :aria-pressed="isSelected(opt.id) ? 'true' : 'false'"
                    :aria-disabled="isAvailable(opt.id) ? 'false' : 'true'"
                    :title="blockedTitle(opt.id)"
                    @click="onClick(opt)"
                >
                    <span v-if="isSelected(opt.id)" class="sel-chip-mark" aria-hidden="true">✓</span>
                    <span class="sel-chip-text">{{ opt.label }}</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * Multi-select pill chips for the candidate application form.
 * - `groups`:       [{ id, label, options: [{ id, label }] }] (label may be empty)
 * - `modelValue`:   array of selected option ids
 * - `availability`: { [id]: { available, blockedBy: [labels] } } from getOptionAvailability()
 * Unavailable chips cannot be selected; tapping one emits `blocked` so the parent
 * can explain why. Selected chips are never removed automatically.
 */
export default {
    name: 'SelectionChips',
    props: {
        groups: { type: Array, default: () => [] },
        modelValue: { type: Array, default: () => [] },
        availability: { type: Object, default: () => ({}) }
    },
    emits: ['update:modelValue', 'blocked'],
    setup(props, { emit }) {
        const isSelected = (id) => props.modelValue.includes(id)
        const info = (id) => props.availability[id] || { available: true, blockedBy: [] }
        const isAvailable = (id) => info(id).available !== false
        const blockedTitle = (id) => isAvailable(id) ? '' : `לא ניתן לשלב עם ${info(id).blockedBy.join(', ')}`

        const onClick = (opt) => {
            if (!isAvailable(opt.id)) {
                emit('blocked', { id: opt.id, label: opt.label, blockedBy: info(opt.id).blockedBy })
                return
            }
            const next = isSelected(opt.id)
                ? props.modelValue.filter(x => x !== opt.id)
                : [...props.modelValue, opt.id]
            emit('update:modelValue', next)
        }

        return { isSelected, isAvailable, blockedTitle, onClick }
    }
}
</script>

<style scoped>
.sel-chips {
    display: flex;
    flex-direction: column;
    gap: 12px;
}
.sel-chips-group-title {
    font-size: 13px;
    font-weight: 800;
    color: #555;
    margin-bottom: 6px;
}
.sel-chips-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.sel-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border-radius: 50px;
    border: 1.5px solid #000;
    background: #fff;
    color: #000;
    font-size: 14px;
    font-weight: 600;
    font-family: inherit;
    line-height: 1.2;
    cursor: pointer;
    transition: background 0.15s, box-shadow 0.15s, transform 0.1s;
}
.sel-chip:hover {
    background: #FFFBC2;
}
.sel-chip:active {
    transform: translateY(1px);
}
.sel-chip.selected {
    background: #FEF200;
    box-shadow: 2px 2px 0 #000;
}
.sel-chip-mark {
    font-size: 12px;
    font-weight: 900;
}
.sel-chip.unavailable,
.sel-chip.unavailable:hover {
    border-style: dashed;
    border-color: #bbb;
    background: #f5f5f5;
    color: #9a9a9a;
    box-shadow: none;
    cursor: not-allowed;
    transform: none;
}
@media (max-width: 420px) {
    .sel-chip {
        padding: 7px 11px;
        font-size: 13px;
    }
}
</style>
